import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { formatKg, formatMetres, kgPerMetre } from '../../utils/coilMath';
import coilService from '../../services/coilService';
import LooseCoilBadge from './LooseCoilBadge';

/**
 * CoilSplitPanel
 *
 * Read-only breakdown of WHICH coils make up a coil-tracked product's stock. The
 * aggregate balance answers "how much"; for these products the operator's real question
 * is "which rolls, and how much is left on each" — that is the whole reason the coil
 * ledger exists, and a bare total hides it.
 *
 * Shared by the product distribution modal and the inventory details modal so the two
 * cannot drift into showing the same stock differently.
 *
 * Deliberately NOT gated on the global coil-tracking switch. That switch governs the
 * creation of new coil work; coils already in stock are simply the truth about this
 * product, and hiding their split while the switch is off would leave the total
 * unexplained.
 *
 * Props:
 * - productSizeId  the product whose coils to show
 * - warehouseId    optional; restrict to one warehouse
 * - expectedKg     optional; the aggregate balance to reconcile against. When it and the
 *                  coil total disagree the panel says so — that difference is the
 *                  central invariant of the feature breaking, and it should be visible
 *                  where someone is already looking at the numbers, not only in a
 *                  nightly report.
 */
const CoilSplitPanel = ({ productSizeId, warehouseId = null, expectedKg = null }) => {
  const { t } = useTranslation();
  const [coils, setCoils] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!productSizeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await coilService.getCoils({
        product_size_id: productSizeId,
        ...(warehouseId ? { warehouse_id: warehouseId } : {}),
        limit: 200,
      });
      if (res.success) {
        setCoils(Array.isArray(res.data?.items) ? res.data.items : []);
      } else {
        setCoils([]);
        setError(res.error || t('coils.ledger.messages.loadFailed'));
      }
    } catch (err) {
      setCoils([]);
      setError(err?.message || t('coils.ledger.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [productSizeId, warehouseId, t]);

  useEffect(() => { load(); }, [load]);

  const totalKg = coils.reduce((sum, c) => sum + (Number(c.remaining_weight_kg) || 0), 0);
  // Whole kg by database constraint, so an exact comparison is meaningful here rather
  // than a tolerance — see the coil-tracking invariant.
  const mismatch = expectedKg !== null && Math.round(Number(expectedKg) || 0) !== Math.round(totalKg);

  if (loading) {
    return (
      <p className="px-1 py-2 text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
        {t('common.actions.loading')}
      </p>
    );
  }

  if (error) {
    return (
      <p className="px-1 py-2 text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{error}</p>
    );
  }

  if (coils.length === 0) {
    return (
      <p className="px-1 py-2 text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
        {t('coils.ledger.empty.title')}
      </p>
    );
  }

  return (
    <div className="mt-3 rounded-xl border p-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide"
              style={{ color: UI_COLORS.TEXT.MUTED }}>
          <FontAwesomeIcon icon={faLayerGroup} />
          {t('coils.challan.coilsLabel')}
        </span>
        <span className="text-xs font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          {t('coils.ledger.totals', { count: coils.length, weight: formatKg(totalKg) })}
        </span>
      </div>

      {mismatch && (
        <div className="mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold"
             style={{
               color: UI_COLORS.STATUS.ERROR,
               backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
             }}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          {t('coils.ledger.reconcile.mismatch', {
            difference: formatKg(Math.abs(Math.round(Number(expectedKg) || 0) - Math.round(totalKg))),
          })}
          <span style={{ color: UI_COLORS.TEXT.MUTED }}>
            ({t('coils.ledger.reconcile.inventoryTotal')} {formatKg(expectedKg)} ·{' '}
            {t('coils.ledger.reconcile.coilTotal')} {formatKg(totalKg)})
          </span>
        </div>
      )}

      <div className="space-y-1">
        {coils.map((coil) => (
          <div key={coil.id}
               className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-2 py-1.5 text-xs"
               style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.18) }}>
            <span className="font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {coil.batch_number}
            </span>
            <LooseCoilBadge coil={coil} />
            <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {formatKg(coil.remaining_weight_kg)} kg
            </span>
            <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {formatMetres(coil.remaining_length_m)} m
            </span>
            {/* This coil's OWN stored ratio. It is never derived from the product
                master: the roll SKUs carry product_weight = 0, so the master has no
                ratio to give. */}
            <span className="rounded px-1.5 py-0.5 font-semibold"
                  style={{
                    color: UI_COLORS.BRAND.PRIMARY,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.12),
                  }}>
              {kgPerMetre(coil)} kg/m
            </span>
              {coil.colour && (
              <span style={{ color: UI_COLORS.TEXT.MUTED }}>{coil.colour}</span>
            )}
            {/* Only when the panel spans warehouses — inside one, naming it on every
                row is noise. */}
            {!warehouseId && coil.warehouse_name && (
              <span className="rounded px-1.5 py-0.5"
                    style={{
                      color: UI_COLORS.TEXT.SECONDARY,
                      backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
                    }}>
                {coil.warehouse_name}
              </span>
            )}
            {coil.supplier_batch_ref && (
              <span style={{ color: UI_COLORS.TEXT.MUTED }}>· {coil.supplier_batch_ref}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CoilSplitPanel;
